import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  useSendConnectionRequestMutation,
  useAcceptConnectionRequestMutation,
} from "@/features/connection/connectionApiSlice";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import InputGroup from "react-bootstrap/InputGroup";
import Form from "react-bootstrap/Form";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import SearchIcon from "@mui/icons-material/Search";

import Timelineflow from "./Component/MergeComponent/Timelineflow";
import { SuccessNotify, FailedNotify } from "../../constants/tostify";
import useTimeline from "../../hooks/useTimeline";
import { useGetApprovedMarketersQuery } from "@/features/marketing/marketingApiSlice";
import { useGetAllConnectedQuery } from "@/features/connection/connectionApiSlice";
import { getAsset } from "@/helpers/helper";
import dummyUser from "../../assets/images/dummy/dummy-user.png";
import { encodeId } from "../../utils/idObfuscator";
import "./timeline.css";

const GENERAL_PUBLIC = "generalPublic";
const SKELETON_COUNT = 3;
const MAX_NAME_LENGTH = 8;
const EMPTY_HIDDEN_TAB_TYPES = [];

const buildDefaultPost = (shopid, userid, businessType) => ({
  shopid: shopid || "",
  userid: userid || "",
  businessType: businessType || GENERAL_PUBLIC,
  my: [{ heading: "", content: "", img: [""], video: [""] }],
});

const formatTabTitle = (key = "") =>
  String(key).charAt(0).toUpperCase() +
  String(key)
    .slice(1)
    .replace(/([A-Z])/g, " $1");

const truncateName = (name = "") =>
  name.length > MAX_NAME_LENGTH
    ? `${name.slice(0, MAX_NAME_LENGTH - 1)}…`
    : name;

const MarketerSkeleton = () => (
  <Row
    className="timeline-marketer-list mt-3 rounded bg-white pb-2 pt-xl-3 pt-lg-1 pt-md-3 pt-sm-3 pt-xs-3 display-setting scroll-container"
    style={{ boxShadow: "0px 0px 5px 1px rgba(0,0,0,0.2)", margin: "1px" }}
  >
    {[...Array(SKELETON_COUNT * 2)].map((_, i) => (
      <Col
        lg="2"
        md="2"
        sm="2"
        xs="2"
        className="timeline-marketer-item ps-2 pe-2"
        key={i}
      >
        <div className="personImgSize">
          <Skeleton circle height="100%" />
        </div>
        <p className="font_11 mt-3 mb-0 text-center">
          <Skeleton width={50} />
        </p>
        <button type="button" className="folow-btn mt-0" disabled>
          <Skeleton height={20} width={60} />
        </button>
      </Col>
    ))}
  </Row>
);

const MarketerCard = ({
  marketer,
  navigate,
  connectedData,
  currentUserId,
  mybusinessType,
}) => {
  const location = useLocation();
  const user = marketer?.userId || {};
  const imageSrc = user.imageUrl?.[0] ? getAsset(user.imageUrl[0]) : dummyUser;
  const displayName = user.fullname || "Anonymous";
  const marketerUserId = user._id ? String(user._id) : null;
  const path = location.pathname || "";
  const connectingAsMarketer =
    path === "/marketing-profile" || path.endsWith("/marketing-profile");
  const senderBusinessType = connectingAsMarketer
    ? "marketerProfile"
    : mybusinessType === "marketerProfile"
      ? "generalPublic"
      : mybusinessType || "generalPublic";

  // ── connection status checks ──
  // Check if there is already a pending (sent) request for this marketer
  const { inRequested, inPending } = useMemo(() => {
    if (!marketerUserId) return { inRequested: false, inPending: false };
    // Check in requested (sent by current user)
    const req = (connectedData?.requested || []).some(
      (r) =>
        String(r.recieverid) === marketerUserId &&
        r.businessType === "marketerProfile",
    );
    // Check pending (received from this marketer, regardless of which profile they sent it from or to)
    const pen = (connectedData?.pending || []).some(
      (r) => String(r.senderid) === marketerUserId,
    );
    return { inRequested: req, inPending: pen };
  }, [marketerUserId, connectedData]);

  const [sendRequest, { isLoading: isSending }] =
    useSendConnectionRequestMutation();
  const [acceptRequest, { isLoading: isAccepting }] =
    useAcceptConnectionRequestMutation();

  const handleNavigate = useCallback(() => {
    marketer.marketerId
      ? navigate(`/view-marketer-profile/${encodeId(marketer.marketerId)}`)
      : FailedNotify("Marketer ID is missing");
  }, [marketer.marketerId, navigate]);

  const handleConnect = useCallback(async () => {
    if (!currentUserId || !marketerUserId) {
      FailedNotify("Unable to send connection request");
      return;
    }
    try {
      const res = await sendRequest({
        senderid: currentUserId,
        recieverid: marketerUserId,
        businessType: "marketerProfile",
        senderBusinessType,
        ...(connectingAsMarketer ? { asMarketerProfile: true } : {}),
      }).unwrap();
      SuccessNotify(res?.message || "Connection request sent!");
    } catch (err) {
      const errMsg =
        err?.data?.message ||
        err?.data?.error ||
        err?.message ||
        "Failed to send connection request";
      FailedNotify(errMsg);
    }
  }, [
    currentUserId,
    marketerUserId,
    sendRequest,
    senderBusinessType,
    connectingAsMarketer,
  ]);

  const handleAccept = useCallback(async () => {
    if (!currentUserId || !marketerUserId) {
      FailedNotify("Unable to accept connection request");
      return;
    }
    try {
      const res = await acceptRequest({
        senderid: marketerUserId,
        recieverid: currentUserId,
        // Accepting on /user-profile keeps receiver as GP so it appears on
        // personal Connectivity; accepting on marketing keeps marketer stamp.
        businessType: connectingAsMarketer
          ? "marketerProfile"
          : "generalPublic",
        senderBusinessType: "marketerProfile",
        ...(connectingAsMarketer ? { asMarketerProfile: true } : {}),
      }).unwrap();
      SuccessNotify(res?.message || "Connection request accepted!");
    } catch (err) {
      const errMsg =
        err?.data?.message ||
        err?.data?.error ||
        err?.message ||
        "Failed to accept connection request";
      FailedNotify(errMsg);
    }
  }, [currentUserId, marketerUserId, acceptRequest, connectingAsMarketer]);

  const isOwnProfile =
    marketerUserId && currentUserId && marketerUserId === String(currentUserId);

  // Decide button label / state
  let buttonLabel = "Connect";
  let buttonDisabled = false;
  let onButtonClick = handleConnect;

  if (isOwnProfile) {
    buttonLabel = "You";
    buttonDisabled = true;
    onButtonClick = undefined;
  } else if (isSending) {
    buttonLabel = "Sending…";
    buttonDisabled = true;
    onButtonClick = undefined;
  } else if (isAccepting) {
    buttonLabel = "Accepting…";
    buttonDisabled = true;
    onButtonClick = undefined;
  } else if (inPending) {
    buttonLabel = "Accept";
    buttonDisabled = false;
    onButtonClick = handleAccept;
  } else if (inRequested) {
    buttonLabel = "Pending";
    buttonDisabled = true;
    onButtonClick = undefined;
  }

  return (
    <Col
      lg="2"
      md="2"
      sm="2"
      xs="2"
      className="timeline-marketer-item ps-2 pe-2"
      key={marketer._id}
    >
      <div
        className="personImgSize"
        onClick={handleNavigate}
        style={{ cursor: "pointer" }}
      >
        <img
          src={imageSrc}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = dummyUser;
          }}
          alt={displayName}
        />
      </div>
      <p className="font_11 mt-3 mb-0 text-center" title={displayName}>
        {truncateName(displayName)}
      </p>
      <button
        type="button"
        className="folow-btn mt-0"
        onClick={onButtonClick}
        disabled={buttonDisabled}
        style={buttonDisabled ? { opacity: 0.65, cursor: "not-allowed" } : {}}
      >
        {buttonLabel}
      </button>
    </Col>
  );
};

const MarketerProfileTab = ({
  approvedLoading,
  approvedMarketers,
  navigate,
  connectedData,
  currentUserId,
  mybusinessType,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const rowClass =
    "timeline-marketer-list mt-3 rounded bg-white pb-2 pt-xl-3 pt-lg-1 pt-md-3 pt-sm-3 pt-xs-3 display-setting";
  const rowStyle = {
    boxShadow: "0px 0px 5px 1px rgba(0,0,0,0.2)",
    margin: "1px",
  };

  const connectedUserIds = useMemo(() => {
    if (!connectedData?.connected) return new Set();
    // Include marketerProfile business type only
    return new Set(
      connectedData.connected
        .filter((c) => c.businessType === "marketerProfile")
        .flatMap((c) => [
          c.senderid ? String(c.senderid) : null,
          c.recieverid ? String(c.recieverid) : null,
        ])
        .filter((id) => id && id !== String(currentUserId)),
    );
  }, [connectedData, currentUserId]);

  const filteredMarketers = useMemo(() => {
    return approvedMarketers.filter((marketer) => {
      const user = marketer?.userId || {};
      const displayName = user.fullname || "Anonymous";
      const marketerUserId = user._id ? String(user._id) : null;

      // Hide the current user's own profile from the list
      if (marketerUserId === String(currentUserId)) return false;

      // Hide already-connected marketers (accepted) from this list
      if (marketerUserId && connectedUserIds.has(marketerUserId)) return false;
      return displayName.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [approvedMarketers, searchTerm, connectedUserIds, currentUserId]);

  if (approvedLoading) return <MarketerSkeleton />;

  return (
    <>
      <Row className="mb-2">
        <Col lg="12" md="12" sm="12" className="">
          <InputGroup className="w-100">
            <Form.Control
              placeholder="Search"
              aria-label="Search"
              aria-describedby="basic-addon1"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <InputGroup.Text id="basic-addon1" className="bg-white">
              <SearchIcon />
            </InputGroup.Text>
          </InputGroup>
        </Col>
      </Row>

      {!filteredMarketers.length ? (
        <Row className={rowClass} style={rowStyle}>
          <Col lg="12" className="text-center p-4">
            <h6 className="text-muted font_small">No new marketer found</h6>
          </Col>
        </Row>
      ) : (
        <Row className={`${rowClass} scroll-container`} style={rowStyle}>
          {filteredMarketers.map((marketer) => (
            <MarketerCard
              key={marketer._id}
              marketer={marketer}
              navigate={navigate}
              connectedData={connectedData}
              currentUserId={currentUserId}
              mybusinessType={mybusinessType}
            />
          ))}
        </Row>
      )}
    </>
  );
};

const TabSkeleton = () => (
  <Tab
    eventKey="skeleton-loading"
    title={
      <div
        className="placeholder-glow d-inline-block"
        style={{ width: "100px", height: "20px" }}
      >
        <span className="placeholder w-100 h-100 rounded"></span>
      </div>
    }
  >
    <div className="p-3">
      {[1, 2].map((i) => (
        <div
          key={i}
          className="border rounded p-3 mb-4 bg-white shadow-sm placeholder-glow"
        >
          {/* Header (Avatar & Name) */}
          <div className="d-flex align-items-center mb-3">
            <span
              className="placeholder rounded-circle"
              style={{ width: "45px", height: "45px" }}
            ></span>
            <div className="ms-3 d-flex flex-column w-100">
              <span className="placeholder col-3 mb-1 rounded"></span>
              <span
                className="placeholder col-2 rounded"
                style={{ height: "0.8em" }}
              ></span>
            </div>
          </div>
          {/* Text Content */}
          <div className="mb-3">
            <span className="placeholder col-12 mb-1 rounded"></span>
            <span className="placeholder col-9 mb-1 rounded"></span>
            <span className="placeholder col-10 rounded"></span>
          </div>
          {/* Media/Image Area */}
          <span
            className="placeholder w-100 rounded mb-3"
            style={{ height: "200px", backgroundColor: "#e9ecef" }}
          ></span>
          {/* Footer Actions */}
          <div className="d-flex gap-4 border-top pt-2 mt-2">
            <span
              className="placeholder col-1 rounded"
              style={{ height: "20px" }}
            ></span>
            <span
              className="placeholder col-1 rounded"
              style={{ height: "20px" }}
            ></span>
            <span
              className="placeholder col-1 rounded"
              style={{ height: "20px" }}
            ></span>
          </div>
        </div>
      ))}
    </div>
  </Tab>
);

const Timeline = ({
  rightSide,
  loading,
  tabsData = [],
  setCreatePost,
  createPost,
  setLoading,
  id,
  mybusinessType,
  hiddenTabTypes = EMPTY_HIDDEN_TAB_TYPES,
  tabTitleOverrides = {},
  activeTab: controlledActiveTab,
  onTabSelect,
}) => {
  const { name } = useSelector((state) => state.user);
  const location = useLocation();
  const navigate = useNavigate();
  const isMounted = useRef(true);
  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );
  const { data: connectedDataResponse } = useGetAllConnectedQuery(
    {
      userid: name?._id,
      ...(id ? { shopid: id } : {}),
    },
    { skip: !name?._id, refetchOnMountOrArgChange: true },
  );

  const isConnected = useMemo(() => {
    if (!id || !connectedDataResponse?.connected) return false;
    return connectedDataResponse.connected.some(
      (c) =>
        c.senderid === id ||
        c.recieverid === id ||
        c.sendershopid === id ||
        c.recievershopid === id,
    );
  }, [id, connectedDataResponse]);

  const [key, setKey] = useState(controlledActiveTab || "home");
  const [show, setShow] = useState(0);
  const [tabsLoading, setTabsLoading] = useState(true);
  // Tabs created after Share when parent tabsData omitted that BT (empty
  // public-against-shop lists drop those keys). Keeps shared posts visible.
  const [ensuredShareTabs, setEnsuredShareTabs] = useState([]);
  const shareFocusRef = useRef(null);

  const [localLoading, setLocalLoading] = useState(false);
  const setLoadingSafe = useMemo(
    () => (typeof setLoading === "function" ? setLoading : setLocalLoading),
    [setLoading],
  );

  const [internalPost, setInternalPost] = useState(() =>
    buildDefaultPost(id, name?._id, mybusinessType),
  );

  useEffect(() => {
    setInternalPost((prev) => {
      const next = {
        ...prev,
        shopid: id || prev.shopid,
        userid: name?._id || prev.userid,
        businessType: mybusinessType || prev.businessType,
      };
      if (
        next.shopid === prev.shopid &&
        next.userid === prev.userid &&
        next.businessType === prev.businessType
      )
        return prev;
      return next;
    });
  }, [id, name?._id, mybusinessType]);

  const createPostState = createPost ?? internalPost;
  const setCreatePostState = setCreatePost ?? setInternalPost;

  const {
    timelineID,
    my: timelineMy,
    sharebyother: timelineShare,
    sharedtoother: timelineSharedToOther,
    businessType,
    loading: timelineLoading,
    refetch: refetchTimeline,
    createPostSubmit,
  } = useTimeline({
    userid: name?._id,
    shopid: id || createPostState?.shopid || null,
    businessType:
      location.pathname === "/view-friends" ? GENERAL_PUBLIC : mybusinessType,
  });

  // Sort newest-first so a freshly created post appears at the top instead
  // of at the bottom (backend stores posts in insertion order).
  const getmyTimeline = useMemo(() => {
    const list = timelineMy ?? [];
    return [...list].sort((a, b) => {
      const dateA = new Date(a?.createdAt || 0);
      const dateB = new Date(b?.createdAt || 0);
      return dateB - dateA;
    });
  }, [timelineMy]);
  const shareTimeline = useMemo(() => timelineShare ?? [], [timelineShare]);
  const sharedToOtherTimeline = useMemo(
    () => timelineSharedToOther ?? [],
    [timelineSharedToOther],
  );
  const getbusinesstype = businessType ?? "";

  const post_ID = useMemo(
    () =>
      [...getmyTimeline, ...shareTimeline].map((post) =>
        key === "home" ? post?._id : post?.postid?._id,
      ),
    [getmyTimeline, shareTimeline, key],
  );

  // Parent tabs often omit BTs with empty suggestion lists. Merge Connectivity
  // + existing share destination BTs + post-share ensured shells so shared
  // posts always have a tab to render on (shop and user profile).
  const effectiveTabsData = useMemo(() => {
    const map = new Map();
    (tabsData || []).forEach((tab) => {
      if (tab?.eventKey) map.set(tab.eventKey, tab);
    });

    const addShell = (bt) => {
      if (!bt || bt === "home") return;
      if (map.has(bt)) return;
      map.set(bt, {
        eventKey: bt,
        title: formatTabTitle(bt),
        data: [],
      });
    };

    (tabsData || []).forEach((tab) => {
      if (!tab?.eventKey) return;
      map.set(tab.eventKey, tab);
    });

    // Shop page: always expose THIS shop's own area tab first.
    // Parent tabsData often only has connected BTs from publicAgainstShop,
    // so shares stamped with mybusinessType had no tab to render on.
    if (id && mybusinessType) {
      addShell(mybusinessType);
    }

    // Always expose General Public tab so connectivity shares and General Public posts
    // have a visible tab on shop pages and personal profile pages.
    addShell(GENERAL_PUBLIC);

    // Always expose Marketer Profile tab when user is a marketer or marketer shares/posts exist
    const hasMarketerShare = [
      ...(sharedToOtherTimeline || []),
      ...(shareTimeline || []),
      ...(getmyTimeline || []),
    ].some(
      (entry) =>
        entry?.businessType === "marketerProfile" ||
        entry?.senderBusinessType === "marketerProfile" ||
        entry?.postid?.bymarketer === true ||
        entry?.bymarketer === true,
    );
    if (
      hasMarketerShare ||
      !!localStorage.getItem("MeAsmarketerId") ||
      mybusinessType === "marketerProfile"
    ) {
      addShell("marketerProfile");
    }

    (connectedDataResponse?.connectedBT || []).forEach((group) => {
      addShell(group?.businessType || group);
    });

    [...(sharedToOtherTimeline || []), ...(shareTimeline || [])].forEach(
      (entry) => {
        addShell(entry?.businessType);
        addShell(entry?.senderBusinessType);
      },
    );

    (ensuredShareTabs || []).forEach((tab) => {
      if (tab?.eventKey && !map.has(tab.eventKey)) map.set(tab.eventKey, tab);
    });

    // Keep shop's own area first; GP near front on personal profiles
    const list = Array.from(map.values());
    if (id && mybusinessType) {
      const ownIdx = list.findIndex(
        (t) =>
          t.eventKey === mybusinessType ||
          String(t.eventKey || "")
            .toLowerCase()
            .replace(/[\s_-]+/g, "") ===
            String(mybusinessType || "")
              .toLowerCase()
              .replace(/[\s_-]+/g, ""),
      );
      if (ownIdx > 0) {
        const [own] = list.splice(ownIdx, 1);
        list.unshift(own);
      }
    } else if (!id && location.pathname !== "/marketing-profile") {
      const gpIdx = list.findIndex((t) => t.eventKey === GENERAL_PUBLIC);
      if (gpIdx > 0) {
        const [gp] = list.splice(gpIdx, 1);
        list.unshift(gp);
      }
    }
    const hiddenTypes = new Set(
      hiddenTabTypes.map((type) =>
        String(type || "")
          .toLowerCase()
          .replace(/[\s_-]+/g, ""),
      ),
    );
    return list.filter(
      (tab) =>
        !hiddenTypes.has(
          String(tab.eventKey || "")
            .toLowerCase()
            .replace(/[\s_-]+/g, ""),
        ),
    );
  }, [
    tabsData,
    connectedDataResponse,
    sharedToOtherTimeline,
    shareTimeline,
    ensuredShareTabs,
    id,
    mybusinessType,
    hiddenTabTypes,
  ]);

  useEffect(() => {
    const norm = (bt) =>
      String(bt || "")
        .toLowerCase()
        .replace(/[\s_-]+/g, "");
    if (effectiveTabsData.length > 0) {
      setTabsLoading(false);
      if (shareFocusRef.current) {
        const focus = shareFocusRef.current;
        const focusTab = effectiveTabsData.find(
          (t) => t.eventKey === focus || norm(t.eventKey) === norm(focus),
        );
        if (focus === "home" || focus === "marketerProfile" || focusTab) {
          const nextKey = focusTab?.eventKey || focus;
          if (key !== nextKey) setKey(nextKey);
          return;
        }
      }
      if (controlledActiveTab) {
        setKey(controlledActiveTab);
      } else if (
        key !== "home" &&
        key !== "marketerProfile" &&
        !effectiveTabsData.some(
          (t) => t.eventKey === key || norm(t.eventKey) === norm(key),
        )
      ) {
        setKey(effectiveTabsData[0].eventKey);
      }
    } else {
      setTabsLoading(false);
      // Empty string must not win over "home" or no tab content renders.
      setKey(controlledActiveTab || "home");
    }
  }, [effectiveTabsData, controlledActiveTab, key]);

  // After share → parent onTabSelect(shop BT): keep local tab key in sync even
  // when tabsData identity is unchanged (controlledActiveTab-only updates).
  // Do not let parent reset away from the tab we just shared onto.
  useEffect(() => {
    if (!controlledActiveTab || controlledActiveTab === key) return;
    if (
      shareFocusRef.current &&
      shareFocusRef.current !== controlledActiveTab
    ) {
      setKey(shareFocusRef.current);
      if (typeof onTabSelect === "function") {
        onTabSelect(shareFocusRef.current);
      }
      return;
    }
    setKey(controlledActiveTab);
  }, [controlledActiveTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeTabData = useMemo(
    () => effectiveTabsData.find((t) => t.eventKey === key)?.data ?? [],
    [key, effectiveTabsData],
  );

  const { data: approvedMarketersData, isLoading: approvedLoading } =
    useGetApprovedMarketersQuery(
      name?._id ? { userId: name._id, sameZone: true } : undefined,
      {
        skip: key !== "marketerProfile" || !name?._id,
      },
    );
  const approvedMarketers = approvedMarketersData?.data ?? [];

  const CreatePostSubmit = useCallback(
    async (customPayload) => {
      setLoadingSafe(true);
      const payload = {
        ...(customPayload || createPostState),
        userid: name?._id || (customPayload || createPostState).userid,
        businessType:
          mybusinessType || (customPayload || createPostState).businessType,
        shopid: id || (customPayload || createPostState).shopid || null,
      };
      try {
        const res = await createPostSubmit(payload);
        if (!isMounted.current) return;
        setLoadingSafe(false);
        setShow(0);
        if (res?.success || res?.post) {
          SuccessNotify("Post Created Successfully...");
          setCreatePostState(buildDefaultPost(id, name?._id, mybusinessType));
        } else {
          FailedNotify("Failed to Create Form");
        }
        refetchTimeline();
      } catch (err) {
        if (!isMounted.current) return;
        setLoadingSafe(false);
        FailedNotify(
          `Failed to Create Form: ${err?.data?.message ?? err?.message ?? JSON.stringify(err)}`,
        );
      }
    },
    [
      createPostState,
      name?._id,
      mybusinessType,
      id,
      createPostSubmit,
      refetchTimeline,
      setLoadingSafe,
      setCreatePostState,
    ],
  );

  const handleShareSuccess = useCallback(
    (selectedBusinessTypes = []) => {
      // /user-profile: focus the area you shared to (shop BT or General Public).
      // Shop pages: ALWAYS focus the shop you shared FROM (mybusinessType),
      // never the modal "who" list (which often includes generalPublic first).
      const norm = (bt) =>
        String(bt || "")
          .toLowerCase()
          .replace(/[\s_-]+/g, "");
      const shopOnly = (selectedBusinessTypes || []).filter(
        (bt) => bt && bt !== "home" && bt !== "generalPublic",
      );
      const preferredBT =
        mybusinessType && norm(mybusinessType) === "marketerprofile"
          ? "marketerProfile"
          : id
            ? mybusinessType ||
              shopOnly[0] ||
              selectedBusinessTypes.find(
                (bt) => bt && bt !== "home" && bt !== "generalPublic",
              )
            : shopOnly[0] ||
              selectedBusinessTypes.find((bt) => bt && bt !== "home") ||
              "generalPublic";
      if (!preferredBT) return;

      const toEnsure = [
        ...new Set(
          [
            preferredBT,
            ...(id
              ? [mybusinessType, preferredBT].filter(Boolean)
              : shopOnly.length
                ? shopOnly
                : ["generalPublic"]),
          ].filter((bt) => bt && bt !== "home"),
        ),
      ];
      setEnsuredShareTabs((prev) => {
        const next = [...prev];
        toEnsure.forEach((bt) => {
          if (!next.some((t) => norm(t.eventKey) === norm(bt))) {
            next.push({
              eventKey: bt,
              title: formatTabTitle(bt),
              data: [],
            });
          }
        });
        return next;
      });

      shareFocusRef.current = preferredBT;
      setKey(preferredBT);
      if (typeof onTabSelect === "function") onTabSelect(preferredBT);
      window.setTimeout(() => {
        if (shareFocusRef.current === preferredBT) {
          shareFocusRef.current = null;
        }
      }, 4000);
    },
    [onTabSelect, id, mybusinessType],
  );

  // ── Shared Timelineflow props ──
  const sharedFlowProps = {
    rightSide,
    getTimelinePosts: refetchTimeline,
    mysetting: key,
    timelineid: timelineID,
    CreatePostSubmit,
    setCreatePost: setCreatePostState,
    id,
    createPost: createPostState,
    show,
    setShow,
    post_ID,
    getmyTimeline,
    setGetmyTimeline: () => {}, // RTK-managed; kept for child API compatibility
    getbusinesstype,
    shareTimeline,
    setShareTimeline: () => {}, // RTK-managed; kept for child API compatibility
    sharedToOtherTimeline,
    mybusinessType,
    timelineLoading,
    onShareSuccess: handleShareSuccess,
  };

  const tabsClassName = `mb-3 ms-1 font_small ${
    Number(rightSide) >= 10 ? "display-setting-2" : "display-setting"
  } scroll-container timeline-business-tabs`;

  return (
    <div className="business-timeline">
      <Tabs
        id="controlled-tab-example"
        activeKey={key}
        onSelect={(k) => {
          shareFocusRef.current = null;
          if (typeof onTabSelect === "function") onTabSelect(k);
          else setKey(k);
        }}
        className={tabsClassName}
      >
      {/* ── My Timeline — always visible (never hide this tab) ── */}
      <Tab eventKey="home" title="My Timeline">
        <Timelineflow {...sharedFlowProps} mysetting="home" data={[]} />
      </Tab>

      {/* ── Marketer Profile — visible on all pages so users can connect with marketers ── */}
      <Tab eventKey="marketerProfile" title="Marketer Profile">
        <div className="p-3 bg-white rounded mb-2">
          <MarketerProfileTab
            approvedLoading={approvedLoading}
            approvedMarketers={approvedMarketers}
            navigate={navigate}
            connectedData={connectedDataResponse}
            currentUserId={name?._id}
            mybusinessType={mybusinessType}
          />
        </div>
        <Timelineflow
          {...sharedFlowProps}
          mysetting="marketerProfile"
          data={[]}
        />
      </Tab>

      {effectiveTabsData.length > 0 || !(loading || tabsLoading) ? (
        effectiveTabsData
          .filter(
            (tab) =>
              tab.eventKey !== "home" && tab.eventKey !== "marketerProfile",
          )
          .map((tab) => (
            <Tab
              eventKey={tab.eventKey}
              title={tabTitleOverrides[tab.eventKey] || tab.title}
              key={tab.eventKey}
            >
              <Timelineflow
                {...sharedFlowProps}
                mysetting={tab.eventKey}
                data={tab.data}
              />
            </Tab>
          ))
      ) : (
        <TabSkeleton />
      )}
      </Tabs>
    </div>
  );
};

export default Timeline;
